import { useQuery } from "@tanstack/react-query";

import type { SubscriptionEstimate } from "features/subscriptions/types";

import { CUSTOMER_QUERY_KEYS } from "common/constants";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { post } from "common/helpers/HTTP";
import type { Pricing } from "common/types";

async function workspaceEstimateCreationRequest(
  workspaceId: string,
  planId: string,
  addonId: string,
  quantity: number,
  type: string,
): Promise<SubscriptionEstimate> {
  const { estimate } = await post(
    `workspaces/${workspaceId}/subscriptions/estimates/create`,
    {
      type,
      items: [
        { id: planId },
        ...(quantity > 0 ? [{ id: addonId, quantity }] : []),
      ],
    },
  );
  return estimate;
}

async function customerEstimateCreationRequest(
  planPricingId: string,
  quantity: number,
): Promise<SubscriptionEstimate> {
  const { estimate } = await post("subscriptions/estimates/create", {
    seat_count: quantity,
    item_price_id: planPricingId,
  });
  return estimate;
}

// if subscriptionId is not provided, this is a customer estimate
export default function useEstimateCreation({
  isCustomerSubscription,
  pricing,
  quantity,
  isEnabled = true,
}: {
  isCustomerSubscription: boolean;
  pricing: { addon: Pricing; plan: Pricing } | undefined;
  quantity: number;
  isEnabled?: boolean;
}) {
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const {
    data: estimateCreation,
    isFetching: isLoadingEstimateCreation,
    error: estimateCreationError,
  } = useQuery({
    queryKey: isCustomerSubscription
      ? CUSTOMER_QUERY_KEYS.subscriptionKeys.creationEstimate(
          pricing?.plan.id,
          quantity,
        )
      : getQueryKeys(workspaceId).subscriptionKeys.creationEstimate(
          pricing?.plan.id,
          quantity,
        ),
    queryFn: isCustomerSubscription
      ? () => customerEstimateCreationRequest(pricing?.plan.id, quantity)
      : () =>
          workspaceEstimateCreationRequest(
            workspaceId,
            pricing?.plan.id,
            pricing?.addon.id,
            quantity,
            pricing?.plan.subscription_type,
          ),
    enabled: !!pricing && isEnabled,
  });

  return { estimateCreation, isLoadingEstimateCreation, estimateCreationError };
}
